/* ============================================================
   Soulskart wishlist layer — localStorage-backed, additive.
   Injects an accessible heart into every <product-card>, persists
   saves, fires a toast, and survives Horizon section re-renders
   (filtering / pagination). No dependencies.
   Public: window.ssWishlist.get() -> [ids], .has(id), .toggle(id)
   Event:  document 'ss:wishlist:change' { detail:{ id, saved, count } }
   ============================================================ */
(() => {
  if (window.ssWishlist) return;
  const KEY = 'ss_wishlist';

  const read = () => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; }
    catch (e) { return []; }
  };
  const write = (arr) => {
    try { localStorage.setItem(KEY, JSON.stringify(arr)); } catch (e) {}
  };
  const has = (id) => read().indexOf(String(id)) !== -1;
  const toggle = (id) => {
    id = String(id);
    const arr = read();
    const i = arr.indexOf(id);
    let saved;
    if (i === -1) { arr.push(id); saved = true; } else { arr.splice(i, 1); saved = false; }
    write(arr);
    document.dispatchEvent(new CustomEvent('ss:wishlist:change', { detail: { id, saved, count: arr.length } }));
    return saved;
  };
  window.ssWishlist = { get: read, has, toggle };

  const HEART = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20.5S3.5 14.7 3.5 9.3A4.3 4.3 0 0 1 12 7a4.3 4.3 0 0 1 8.5 2.3c0 5.4-8.5 11.2-8.5 11.2Z"/></svg>';

  const sync = (btn, saved) => {
    btn.classList.toggle('is-on', saved);
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    btn.setAttribute('aria-label', saved ? 'Remove from wishlist' : 'Save to wishlist');
  };

  const enhance = (card) => {
    if (!card || card.dataset.ssWish) return;
    const id = card.getAttribute('data-product-id');
    if (!id) return;
    card.dataset.ssWish = '1';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ss-wish';
    btn.innerHTML = HEART;
    sync(btn, has(id));

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const saved = toggle(id);
      sync(btn, saved);
      if (saved) {
        btn.classList.remove('just-saved');
        void btn.offsetWidth;           // restart animation
        btn.classList.add('just-saved');
      }
      if (typeof window.ssToast === 'function') {
        window.ssToast(saved ? 'Saved to your wishlist.' : 'Removed from wishlist.', {
          type: saved ? 'ok' : 'info',
          action: saved ? { label: 'Undo', onClick: () => { toggle(id); sync(btn, has(id)); } } : null,
        });
      }
    });

    card.insertBefore(btn, card.firstChild);
  };

  const scan = (root) => {
    (root || document).querySelectorAll('product-card:not([data-ss-wish])').forEach(enhance);
    // a11y: label Horizon's icon-only filter-drawer close button
    document.querySelectorAll('.facets-drawer__close:not([aria-label])').forEach((b) => {
      if (!(b.textContent || '').trim()) b.setAttribute('aria-label', 'Close filters');
    });
  };

  // Keep hearts in sync across duplicate cards (same product in multiple rails)
  document.addEventListener('ss:wishlist:change', (e) => {
    const { id, saved } = e.detail;
    document.querySelectorAll('product-card[data-product-id="' + id + '"] .ss-wish').forEach((b) => sync(b, saved));
  });

  // Cart-count bubble: pop when the count increases (add-to-cart) ---
  const watchCartBubble = () => {
    const icon = document.querySelector('cart-icon, .header-actions__cart-icon');
    if (!icon) return;
    const countEl = () => icon.querySelector('.cart-bubble__text-count');
    const num = () => { const el = countEl(); return el ? parseInt((el.textContent || '').replace(/\D/g, ''), 10) || 0 : 0; };
    let last = num();
    const pop = () => {
      const bubble = icon.querySelector('.cart-bubble');
      if (!bubble) return;
      bubble.classList.remove('ss-pop');
      void bubble.offsetWidth;
      bubble.classList.add('ss-pop');
    };
    const mo = new MutationObserver(() => {
      const now = num();
      if (now > last) pop();
      last = now;
    });
    mo.observe(icon, { childList: true, subtree: true, characterData: true });
  };

  const init = () => {
    scan(document);
    watchCartBubble();
    // Re-scan after Horizon re-renders sections (filters, pagination, quick views)
    let raf;
    const mo = new MutationObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => scan(document));
    });
    mo.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', () => scan(document));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
